import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import Photo from "@/components/Photo";
import JsonLd from "@/components/JsonLd";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import { DoctolibLogo } from "@/components/DoctolibLogo";
import useSeo, { SITE_URL } from "@/hooks/useSeo";
import { SITE } from "@/constants/site";
import useI18n from "@/i18n";
import { HTML_LANG, pagePath } from "@/i18n/config";

const Bloc = ({ titre, lignes, testid }) => (
    <div data-testid={testid}>
        <h3 className="text-lg font-bold text-olivet">{titre}</h3>
        <hr className="rule-gold mt-3 w-12" />
        <ul className="mt-3">
            {lignes.map(([annee, intitule, etab]) => (
                <li
                    key={`${annee}-${intitule}`}
                    className="flex gap-4 border-b py-3 last:border-0"
                    style={{ borderColor: "var(--c-line)" }}
                >
                    <span className="w-12 shrink-0 font-display text-lg text-terrat">{annee}</span>
                    <span>
                        <span className="block font-semibold">{intitule}</span>
                        <span className="block text-sm text-ink2">{etab}</span>
                    </span>
                </li>
            ))}
        </ul>
    </div>
);

export default function MedecinPage() {
    const { t, lang } = useI18n();
    const page = t.pages.medecin;
    const path = pagePath(lang, "medecin");

    useSeo({
        title: t.seo.medecin.title,
        description: t.seo.medecin.description,
        path,
        lang,
        pageKey: "medecin",
    });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <JsonLd
                id="ld-physician"
                data={{
                    "@context": "https://schema.org",
                    "@type": "Physician",
                    name: SITE.doctor,
                    jobTitle: page.jobTitle,
                    url: `${SITE_URL}${path}`,
                    image: `${SITE_URL}/images/medecin.jpg`,
                    medicalSpecialty: ["PlasticSurgery", "Dermatology", "Endocrine", "PrimaryCare"],
                    identifier: { "@type": "PropertyValue", name: "RPPS", value: SITE.rpps },
                    worksFor: {
                        "@type": "MedicalClinic",
                        name: SITE.name,
                        url: `${SITE_URL}${pagePath(lang, "home")}`,
                    },
                    address: {
                        "@type": "PostalAddress",
                        streetAddress: "10 rue Vladimir Kramnik",
                        postalCode: "92600",
                        addressLocality: "Asnières-sur-Seine",
                        addressCountry: "FR",
                    },
                    alumniOf: t.formation.diplomes.map(([, , etab]) => ({
                        "@type": "EducationalOrganization",
                        name: etab,
                    })),
                    knowsLanguage: HTML_LANG[lang],
                }}
            />
            <Header />
            <main className="pt-24" data-testid="page-medecin">
                <div className="mx-auto max-w-[940px] px-5 pb-20 pt-6">
                    <PageBreadcrumb label={t.nav.medecin} pageKey="medecin" />

                    <div className="mt-8 grid items-center gap-10 md:grid-cols-[300px_1fr] md:gap-14">
                        <Reveal>
                            <Photo
                                file="medecin.jpg"
                                alt={page.photoAlt}
                                position="50% 20%"
                                ratioClass="arch aspect-[4/5]"
                                frameStyle={{ border: "2px solid var(--c-gold)", borderBottom: "none" }}
                                className="mx-auto w-full max-w-[240px] md:mx-0 md:max-w-[300px]"
                                eager
                            />
                        </Reveal>
                        <Reveal delay={0.08}>
                            <p className="eyebrow">{page.eyebrow}</p>
                            <h1 className="title-grad mt-3 text-4xl sm:text-5xl">{page.h1}</h1>
                            <hr className="rule-gold mt-5 w-24" />
                            <p className="mt-5 text-lg text-ink2">{page.lead}</p>
                            <ul className="mt-6 flex flex-wrap gap-2.5" data-testid="medecin-page-tags">
                                {t.formation.expertises.map((item) => (
                                    <li
                                        key={item}
                                        className="rounded-full bg-white/70 px-4 py-1.5 text-sm text-ink"
                                        style={{ border: "1px solid rgba(201,162,75,0.45)" }}
                                    >
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-6 text-sm text-ink2" data-testid="medecin-page-rpps">
                                {page.rpps}
                            </p>
                        </Reveal>
                    </div>

                    <Reveal>
                        <h2 className="mt-16 text-2xl text-terrat md:text-3xl">
                            {page.approcheTitle}
                        </h2>
                        <hr className="rule-gold mt-3 w-12" />
                        <div className="mt-4 space-y-4 text-ink2">
                            {page.approche.map((p) => (
                                <p key={p.slice(0, 40)}>{p}</p>
                            ))}
                        </div>
                    </Reveal>

                    <div className="mt-14 space-y-10">
                        <Reveal>
                            <Bloc
                                titre={t.formation.diplomesTitle}
                                lignes={t.formation.diplomes}
                                testid="medecin-page-diplomes"
                            />
                        </Reveal>
                        <Reveal>
                            <Bloc
                                titre={t.formation.autresTitle}
                                lignes={t.formation.autres}
                                testid="medecin-page-autres"
                            />
                        </Reveal>
                    </div>

                    <Reveal>
                        <div className="card mt-14 p-7">
                            <h2 className="text-2xl text-terrat">{page.ctaTitle}</h2>
                            <hr className="rule-gold mt-3 w-12" />
                            <p className="mt-4 text-ink2">{page.ctaText}</p>
                            <a
                                href={SITE.doctolibUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary mt-6"
                                data-testid="medecin-page-rdv-button"
                            >
                                {t.common.rdvOn}
                                <DoctolibLogo height={15} color="#FFFFFF" />
                            </a>
                        </div>
                    </Reveal>
                </div>
            </main>
            <Footer />
        </>
    );
}
